import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { label: 'How It Works', action: () => scrollToSection('how-it-works') },
    { label: 'Benefits', action: () => scrollToSection('benefits') },
    { label: 'About Us', action: () => scrollToSection('about') },
    { label: 'Pricing', action: () => scrollToSection('pricing') },
    { label: 'Contact', action: () => scrollToSection('contact') },
    { label: 'FAQ', action: () => scrollToSection('faq') },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/931a6a5b8787d994e77291d474d70bba.jpg)'
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1>
              <img 
                src="https://public.readdy.ai/ai/img_res/82f8a00e-bc28-4af6-bf3f-740e76e3ab6b.png" 
                alt="OverUnder Roofing – Independent Roofing Advocate serving Wake County NC" 
                className="h-12 w-auto"
                title="OverUnder Roofing - The House Always Wins"
              />
            </h1>
            <div>
              <p className={`text-xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                OverUnder Roofing
              </p>
              <p className={`text-sm italic transition-colors ${scrolled ? 'text-gray-600' : 'text-white/90'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
                The House Always Wins
              </p>
            </div>
          </div>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            {navItems.map(({ label, action }) => (
              <button
                key={label}
                onClick={action}
                className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}
              >
                {label}
              </button>
            ))}
            <Link
              to="/wake-county-simulation"
              className={`text-sm font-medium transition-colors hover:opacity-80 whitespace-nowrap cursor-pointer ${scrolled ? 'text-emerald-700' : 'text-emerald-300'}`}
            >
              Market Data
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {navItems.map(({ label, action }) => (
                <button
                  key={label}
                  onClick={action}
                  className="text-left text-sm font-medium text-gray-700 hover:text-emerald-600 py-3 border-b border-gray-50 cursor-pointer whitespace-nowrap"
                >
                  {label}
                </button>
              ))}
              <Link
                to="/wake-county-simulation"
                onClick={() => setMobileOpen(false)}
                className="text-left text-sm font-medium text-emerald-700 hover:text-emerald-600 py-3 cursor-pointer whitespace-nowrap"
              >
                Market Data
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full mb-8">
              <i className="ri-home-4-line text-lg text-emerald-700"></i>
              <span className="text-sm font-medium text-emerald-700">Wake County's Independent Roofing Advocate</span>
            </div>

            <h2 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Stop Guessing on Roofers. Get the Right Roof at the Right Price.
            </h2>

            <p className="text-lg text-white/90 mb-6 leading-relaxed max-w-xl">
              We act as your independent roofing advocate—collecting and analyzing multiple bids so you avoid costly mistakes and get the best value.
            </p>

            <ul className="mb-10 space-y-3">
              {[
                { icon: 'ri-money-dollar-circle-line', text: 'Avoid overpaying by thousands' },
                { icon: 'ri-bar-chart-grouped-line', text: 'Compare bids clearly (not just price)' },
                { icon: 'ri-shield-user-line', text: 'Get expert help without contractor bias' },
              ].map(({ icon, text }) => (
                <li key={text} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-500/30 flex-shrink-0">
                    <i className={`${icon} text-emerald-300 text-sm`}></i>
                  </div>
                  <span className="text-white/90 font-medium">{text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-5">
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-2xl font-semibold text-base transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Started - $500 Flat Fee
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold text-base hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                How It Works
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20residential%20roofing%20installation%20close%20up%20view%20quality%20shingles%20craftsmanship%20detail%20worker%20installing%20roof%20tiles%20blue%20sky%20background%20construction%20excellence%20home%20improvement%20architectural%20detail%20clean%20professional%20work%20bright%20natural%20lighting&width=800&height=600&seq=hero-img-001&orientation=landscape"
                alt="Professional roofing installation in Cary NC – Quality craftsmanship and vetted contractors"
                title="OverUnder Roofing – Professional Roof Installation Wake County NC"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 bg-gray-900/90 backdrop-blur-sm px-5 py-3 rounded-xl flex items-center gap-3">
                <i className="ri-shield-check-line text-2xl text-emerald-400"></i>
                <span className="text-white font-semibold text-sm">No Commission Conflicts</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}